/**
 * DevToolsPopover - Combined Git, AI Status, Activity Heatmap, and Debug info in one popover
 *
 * Consolidates dev tools into a single button with three tabs: Activity, Status, Debug.
 * Shows a status dot when AI providers have issues.
 */

import { useState } from "react"
import { useLocation } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { useAuth } from "@/hooks/useAuth"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  Bug,
  Copy,
  Check,
  Code2,
  Server,
  Monitor,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { ActivityHeatmapGrid } from "./activity-heatmap-grid"
import { ActivityHeatmapModal } from "./activity-heatmap-modal"

// Git info injected at build time
const commitHash = __GIT_COMMIT_HASH__
const commitDate = __GIT_COMMIT_DATE__
const commitMessage = __GIT_COMMIT_MESSAGE__

function getRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return ""
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

export function DevToolsPopover() {
  const queryClient = useQueryClient()
  const { currentLicense, user } = useAuth()
  const location = useLocation()
  const [copied, setCopied] = useState(false)
  const [activeTab, setActiveTab] = useState<"activity" | "status" | "debug">("status")
  const [heatmapView, setHeatmapView] = useState<"activity" | "development">("activity")
  const [modalOpen, setModalOpen] = useState(false)
  // These three queries used to fire on app load for every user, whether or
  // not they ever opened this popover — including a 90-day heatmap and a
  // provider-status poll that ran every 5 minutes forever. Nothing here is
  // needed until the popover is actually open.
  const [open, setOpen] = useState(false)

  // Backend health/deploy info
  const { data: backendHealth } = useQuery({
    queryKey: ["backend-health"],
    queryFn: () => api.health.get(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: open,
  })

  // AI provider status
  const { data: aiData } = useQuery({
    queryKey: ["ai-provider-status"],
    queryFn: () => api.usage.providerStatus(),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
    enabled: open,
  })

  // Activity heatmap data — cached for 10 min, kept in cache for 30 min
  const { data: activityData } = useQuery({
    queryKey: ["activity-heatmap", 90],
    queryFn: () => api.activityHeatmap.getActivity(90),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: open,
  })

  // Commits data — only fetched when development tab selected
  const { data: commitsData } = useQuery({
    queryKey: ["commits-heatmap", 90],
    queryFn: () => api.activityHeatmap.getCommits(90),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: open && heatmapView === "development",
  })

  const refreshMutation = useMutation({
    mutationFn: () => api.usage.refreshHealthCheck(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-provider-status"] })
    },
  })

  const overallStatus = aiData?.overall_status ?? "healthy"
  const hasIssue = overallStatus !== "healthy"

  // Heatmap derived state
  const heatmapData = heatmapView === "activity" ? activityData : commitsData
  const heatmapLabel = heatmapView === "activity" ? "actions" : "commits"

  // Git info
  const relativeTime = getRelativeTime(commitDate)
  // Debug info
  const debugData = currentLicense ? {
    user_id: user?.id || "N/A",
    user_email: user?.email || "N/A",
    site_id: currentLicense.site_id,
    site_domain: currentLicense.domain || "No domain",
    license_key: currentLicense.key,
    current_route: location.pathname,
    api_endpoint: "https://api.gluska.co",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    timestamp: new Date().toISOString(),
  } : null

  const copyDebug = () => {
    if (!debugData) return
    const text = Object.entries(debugData)
      .map(([key, value]) => `${key}: ${value}`)
      .join("\n")
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const formatLastCheck = (timestamp: string | null) => {
    if (!timestamp) return "Never"
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    if (diffMins < 1) return "Just now"
    if (diffMins < 60) return `${diffMins}m ago`
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours}h ago`
    return date.toLocaleDateString()
  }

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 relative text-muted-foreground hover:text-foreground"
          title="Dev Tools"
        >
          <Code2 className="h-4 w-4" />
          {hasIssue && (
            <span className={cn(
              "absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full",
              overallStatus === "error" ? "bg-red-500" : "bg-amber-500"
            )} />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-3">
          {/* Tab toggle — 3 tabs */}
          <div className="flex items-center gap-1 p-0.5 bg-muted rounded-lg">
            <button
              onClick={() => setActiveTab("activity")}
              className={cn(
                "flex-1 px-3 py-1 text-xs font-medium rounded-md transition-colors",
                activeTab === "activity"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Activity
            </button>
            <button
              onClick={() => setActiveTab("status")}
              className={cn(
                "flex-1 px-3 py-1 text-xs font-medium rounded-md transition-colors",
                activeTab === "status"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Status
            </button>
            <button
              onClick={() => setActiveTab("debug")}
              className={cn(
                "flex-1 px-3 py-1 text-xs font-medium rounded-md transition-colors",
                activeTab === "debug"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Debug
            </button>
          </div>

          {activeTab === "activity" ? (
            <>
              {/* Activity/Development sub-toggle */}
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Heatmap</div>
                <div className="flex items-center gap-1 p-0.5 bg-muted rounded-md">
                  <button
                    onClick={() => setHeatmapView("activity")}
                    className={cn(
                      "px-2 py-0.5 text-[10px] font-medium rounded transition-colors",
                      heatmapView === "activity"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    Activity
                  </button>
                  <button
                    onClick={() => setHeatmapView("development")}
                    className={cn(
                      "px-2 py-0.5 text-[10px] font-medium rounded transition-colors",
                      heatmapView === "development"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    Development
                  </button>
                </div>
              </div>

              {/* Heatmap grid */}
              {heatmapData ? (
                <ActivityHeatmapGrid
                  data={heatmapData.data}
                  days={90}
                  label={heatmapLabel}
                />
              ) : (
                <div className="flex items-center justify-center h-24">
                  <div className="text-xs text-muted-foreground">Loading...</div>
                </div>
              )}

              {/* Summary stats */}
              {heatmapData && (
                <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-1 border-t">
                  <span>
                    <span className="font-medium text-foreground">{heatmapData.total.toLocaleString()}</span> {heatmapLabel} in 90 days
                  </span>
                  <span>
                    <span className="font-medium text-foreground">{heatmapData.active_days}</span> active days
                  </span>
                </div>
              )}

              {/* View full year */}
              <button
                onClick={() => setModalOpen(true)}
                className="w-full text-[10px] text-muted-foreground hover:text-foreground text-center py-1 transition-colors"
              >
                View full year &rarr;
              </button>
            </>
          ) : activeTab === "status" ? (
            <>
              {/* Deploy Section */}
              <div className="space-y-2">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Deploy</div>

                {/* Frontend */}
                <div className="space-y-0.5">
                  <div className="flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground">
                    <Monitor className="h-3 w-3" />
                    Frontend
                  </div>
                  <div className="flex items-center gap-2 text-sm pl-[18px]">
                    <span className="font-mono text-xs">{commitHash}</span>
                    {relativeTime && <span className="text-xs text-muted-foreground">{relativeTime}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate pl-[18px]" title={commitMessage}>{commitMessage}</p>
                </div>

                {/* Backend */}
                <div className="space-y-0.5">
                  <div className="flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground">
                    <Server className="h-3 w-3" />
                    Backend
                  </div>
                  {backendHealth?.git?.hash ? (
                    <>
                      <div className="flex items-center gap-2 text-sm pl-[18px]">
                        <span className="font-mono text-xs">{backendHealth.git.hash}</span>
                        {backendHealth.git.date && (
                          <span className="text-xs text-muted-foreground">{getRelativeTime(backendHealth.git.date)}</span>
                        )}
                      </div>
                      {backendHealth.git.message && (
                        <p className="text-xs text-muted-foreground truncate pl-[18px]" title={backendHealth.git.message}>
                          {backendHealth.git.message}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground pl-[18px]">
                      {backendHealth ? `v${backendHealth.version}` : "Loading..."}
                    </p>
                  )}
                </div>
              </div>

              <div className="border-t" />

              {/* AI Provider Status Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">AI Providers</div>
                  {aiData && (
                    <div className={cn("flex items-center gap-1",
                      overallStatus === "error" ? "text-red-500" :
                      overallStatus === "warning" ? "text-amber-500" : "text-green-500"
                    )}>
                      {overallStatus === "error" ? <XCircle className="h-3 w-3" /> :
                       overallStatus === "warning" ? <AlertTriangle className="h-3 w-3" /> :
                       <CheckCircle2 className="h-3 w-3" />}
                      <span className="text-[10px] font-medium capitalize">{overallStatus}</span>
                    </div>
                  )}
                </div>

                {aiData?.critical_errors && aiData.critical_errors.length > 0 && (
                  <div className="rounded-md bg-red-50 dark:bg-red-950/30 p-2 space-y-1">
                    {aiData.critical_errors.map((error, i) => (
                      <div key={i} className="flex items-start gap-1.5 text-xs text-red-700 dark:text-red-400">
                        <XCircle className="h-3 w-3 mt-0.5 shrink-0" />
                        <span>{error.message}</span>
                      </div>
                    ))}
                  </div>
                )}

                {aiData?.providers && Object.entries(aiData.providers).map(([name, provider]) => {
                  const displayName = { openai: "OpenAI", anthropic: "Claude", perplexity: "Perplexity" }[name] || name
                  const prov = provider as { status: string; calls_24h: { total: number }; cost_30d: number }
                  const color = prov.status === "error" ? "text-red-500" : prov.status === "warning" ? "text-amber-500" : "text-green-500"
                  const Icon = prov.status === "error" ? XCircle : prov.status === "warning" ? AlertTriangle : CheckCircle2
                  return (
                    <div key={name} className="flex items-center justify-between py-0.5">
                      <div className="flex items-center gap-1.5">
                        <Icon className={cn("h-3 w-3", color)} />
                        <span className="text-xs">{displayName}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        <span>{prov.calls_24h.total} calls/24h</span>
                        <span>${prov.cost_30d.toFixed(2)}</span>
                      </div>
                    </div>
                  )
                })}

                {aiData && (
                  <>
                    <div className="flex justify-between items-center text-xs pt-1 border-t">
                      <span className="text-muted-foreground">30-day spend</span>
                      <span className="font-medium">${aiData.total_cost_30d.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] text-muted-foreground">
                        Last check: {formatLastCheck(aiData.last_health_check)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => refreshMutation.mutate()}
                        disabled={refreshMutation.isPending}
                        className="h-6 text-[10px] gap-1 px-2"
                      >
                        {refreshMutation.isPending ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3 w-3" />
                        )}
                        {refreshMutation.isPending ? "Checking..." : "Refresh"}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Debug Tab */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Bug className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium">Debug Info</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyDebug}
                  className="h-6 text-[10px] gap-1 px-2"
                >
                  {copied ? (
                    <><Check className="h-3 w-3" /> Copied!</>
                  ) : (
                    <><Copy className="h-3 w-3" /> Copy All</>
                  )}
                </Button>
              </div>

              {debugData ? (
                <div className="space-y-2">
                  <DebugSection title="User">
                    <DebugRow label="Email" value={debugData.user_email} />
                    <DebugRow label="User ID" value={String(debugData.user_id)} />
                  </DebugSection>
                  <DebugSection title="Site">
                    <DebugRow label="Site ID" value={String(debugData.site_id)} />
                    <DebugRow label="Domain" value={debugData.site_domain} />
                    <DebugRow label="License" value={debugData.license_key} small />
                  </DebugSection>
                  <DebugSection title="App">
                    <DebugRow label="Route" value={debugData.current_route} />
                    <DebugRow label="API" value={debugData.api_endpoint} />
                    <DebugRow label="Viewport" value={debugData.viewport} />
                  </DebugSection>
                  <div className="pt-1 border-t text-[10px] text-muted-foreground">
                    {new Date(debugData.timestamp).toLocaleString()}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No site selected</p>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>

    <ActivityHeatmapModal
      open={modalOpen}
      onOpenChange={setModalOpen}
      defaultTab={heatmapView}
    />
    </>
  )
}

function DebugSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{title}</div>
      {children}
    </div>
  )
}

function DebugRow({ label, value, small = false }: { label: string; value: string; small?: boolean }) {
  return (
    <div className="flex justify-between items-center gap-2">
      <span className="text-[10px] text-muted-foreground shrink-0">{label}:</span>
      <span className={cn("font-mono text-right truncate", small ? "text-[9px]" : "text-[10px]")}>{value}</span>
    </div>
  )
}
